import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { TypographyStyles } from '@/styles'
import { Alert, Button, Card, CardColors, Dialog, List, Text } from '@/ui'

import { getCurrentUser } from '@/selectors/user.selectors'
import { selectHeardProductSubscription } from '@/reducers/subscription.slice'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { submitCancellationRequest } from '@/actions/settings/billingActions'
import { updateMembership } from '@/actions/userActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { USER_TRANSACTION_KEY } from '@/features/transactions/transactions.slice'

import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { selectIsPayrollActive } from '@/features/payroll/payroll.selectors'
import { ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import {
  selectHasBlockingBankingProfile,
  selectUserHasActiveAnnualCommitment,
} from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { hasTaxfyleJobOrExtensionInProgress } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { selectBankingApplicationStatus } from '@/features/banking/banking.selectors'
import { BankingApplicationStatus } from '@/features/banking/banking.types'

interface ConfirmManualCancellationDialogProps {
  hasContractorFilingInProgress: boolean
}

export const ConfirmManualCancellationDialog = ({
  hasContractorFilingInProgress,
}: ConfirmManualCancellationDialogProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const currentUser = useReselector(getCurrentUser)
  const subscription = useReselector(selectHeardProductSubscription)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const hasTaxfyleJob = useReselector(
    hasTaxfyleJobOrExtensionInProgress,
    currentTaxYear
  )

  const isLoadingTransactionsAndUserTaxEstimates = useReselector(
    selectIsFetchingForKeys,
    [USER_TRANSACTION_KEY, ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY]
  )

  const hasActiveAnnualCommitment = useReselector(
    selectUserHasActiveAnnualCommitment
  )

  const isActiveGEPUser = useReselector(selectIsPayrollActive)

  const hasBlockingBanking = useReselector(selectHasBlockingBankingProfile)
  const bankingStatus = useReselector(selectBankingApplicationStatus)

  const hasNonBankingBlocker =
    hasActiveAnnualCommitment ||
    isActiveGEPUser ||
    hasTaxfyleJob ||
    hasContractorFilingInProgress
  const hasExplainedBlocker = hasBlockingBanking || hasNonBankingBlocker
  const showSubmitRequest = hasNonBankingBlocker || !hasExplainedBlocker

  useEffect(() => {
    pageView('confirm cancellation modal')
  }, [pageView])

  const cancelRequest = async () => {
    setDisabled(true)
    setLoading(true)
    const res = await submitCancellationRequest(currentUser?.id)(dispatch)
    track('submit manual cancellation request')

    if (res) {
      setDisabled(false)
      setLoading(false)

      await updateMembership(currentUser?.id, {
        manualCancellationRequestedAt: DateTime.now().toString(),
      })(dispatch)

      sendUserCancellationMessageZap(currentUser, 'manual_cancel')
      close()
      location.reload()
    } else {
      setError('We were unable to submit your cancellation request.')
      setDisabled(false)
      setLoading(false)
    }
  }

  const endDate = subscription?.nextBillingDate
    ? DateTime.fromSeconds(subscription?.nextBillingDate).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_SHORT
      )
    : 'the end of your billing period'

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button
          variant="link"
          onClick={() => {
            sendUserCancellationMessageZap(currentUser, 'clicked_canceled')
            track('click cancel plan')
          }}
          disabled={isLoadingTransactionsAndUserTaxEstimates}
        >
          Cancel Plan
        </Button>
      </Dialog.Trigger>

      <Dialog.Content>
        <Dialog.Title>
          Please note: We cannot process your cancellation automatically
        </Dialog.Title>

        {error && <Alert variant="error" description={error} />}

        <Dialog.Description>
          {hasBlockingBanking && (
            <>
              <Text variant={TypographyStyles.BODY_LG}>
                {bankingStatus === BankingApplicationStatus.Approved
                  ? 'You have an active Heard Banking account. Before you can cancel your Heard subscription, contact Heard Banking support to close your bank account.'
                  : 'You have an in-progress Heard Banking application. Before you can cancel your Heard subscription, contact Heard Banking support to cancel your application.'}
              </Text>

              <br />

              <Button
                variant="primary"
                className="mb-5"
                onClick={() => navigate('/banking')}
              >
                Go to Heard Banking
              </Button>

              <br />
            </>
          )}

          {hasTaxfyleJob && (
            <Text variant={TypographyStyles.BODY_LG}>
              Since we have already started filing your {currentTaxYear} taxes,
              we cannot automatically cancel your subscription until your taxes
              are transmitted to the IRS. You have until {endDate} to get your
              taxes fully filed before your next renewal.
            </Text>
          )}

          {hasContractorFilingInProgress && (
            <Text variant={TypographyStyles.BODY_LG}>
              You have a {currentTaxYear} 1099 contractor filing in progress, so
              we cannot automatically cancel your subscription until it is
              complete.
            </Text>
          )}

          {isActiveGEPUser && (
            <Text variant={TypographyStyles.BODY_LG}>
              You have active payroll with Heard, so we cannot automatically
              cancel your subscription.
            </Text>
          )}

          {hasActiveAnnualCommitment && (
            <>
              <Text variant={TypographyStyles.BODY_LG}>
                Your contract includes an annual commitment to Heard.
              </Text>

              <Card color={CardColors.WARNING}>
                <Text className="bold">What does this mean?</Text>

                <List>
                  <List.Item>
                    <li>Refunds are not available.</li>
                  </List.Item>

                  <List.Item>
                    <li>
                      You may owe the remaining balance of your subscription.
                    </li>
                  </List.Item>
                </List>
              </Card>
            </>
          )}

          {!hasExplainedBlocker && (
            <Text variant={TypographyStyles.BODY_LG}>
              We were unable to confirm your account details, so our Support
              team needs to review your cancellation.
            </Text>
          )}

          {showSubmitRequest && (
            <Text variant={TypographyStyles.BODY_LG}>
              If you want to continue, you can submit a request to cancel your
              subscription.
            </Text>
          )}
        </Dialog.Description>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary">Close</Button>
          </Dialog.Close>

          {showSubmitRequest && (
            <Button
              variant="primary"
              onClick={cancelRequest}
              disabled={disabled}
              loading={loading}
            >
              Submit Request
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
